const mobileTrayQuery = window.matchMedia('(max-width: 749px)');

class PdpMobileTray {
  /**
   * @param {HTMLElement} host
   */
  constructor(host) {
    this.host = host;
    this.tray = host.querySelector('[data-pdp-mobile-tray]');
    this.sentinel = host.querySelector('[data-pdp-mobile-tray-sentinel]');
    this.mount = host.querySelector('[data-pdp-mobile-variant-mount]');
    this.badge = host.querySelector('.variant-option--material-badges');
    this.badgeOriginParent = this.badge?.parentElement ?? null;
    this.badgeOriginNextSibling = this.badge?.nextElementSibling ?? null;
    this.dockStart = 0;
    this.rafId = 0;
    this.resizeObserver = new ResizeObserver(() => {
      this.measure();
    });
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.updateDockState = this.updateDockState.bind(this);

    if (this.tray instanceof HTMLElement) {
      this.resizeObserver.observe(this.tray);
    }

    if (this.badge instanceof HTMLElement) {
      this.resizeObserver.observe(this.badge);
    }

    mobileTrayQuery.addEventListener('change', this.handleMediaChange);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.syncBadgePlacement();
    this.measure();
  }

  disconnect() {
    this.resizeObserver.disconnect();
    mobileTrayQuery.removeEventListener('change', this.handleMediaChange);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll);
    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
    this.restoreBadge();
    this.host.style.removeProperty('--pdp-mobile-tray-height');
    this.host.style.removeProperty('--pdp-mobile-variant-badge-bottom');
    this.host.classList.remove('is-docked');
  }

  handleMediaChange() {
    this.syncBadgePlacement();
    this.measure();
  }

  handleResize() {
    this.syncBadgePlacement();
    this.measure();
  }

  measure() {
    if (!(this.tray instanceof HTMLElement) || !mobileTrayQuery.matches) {
      this.host.style.removeProperty('--pdp-mobile-tray-height');
      this.host.style.removeProperty('--pdp-mobile-variant-badge-bottom');
      this.host.classList.remove('is-docked');
      return;
    }

    const trayHeight = Math.ceil(this.tray.offsetHeight);
    this.host.style.setProperty('--pdp-mobile-tray-height', `${trayHeight}px`);

    if (this.badge instanceof HTMLElement) {
      const trayRect = this.tray.getBoundingClientRect();
      const badgeRect = this.badge.getBoundingClientRect();
      const badgeBottom = Math.max(0, badgeRect.bottom - trayRect.top);

      this.host.style.setProperty('--pdp-mobile-variant-badge-bottom', `${badgeBottom}px`);
    } else {
      this.host.style.removeProperty('--pdp-mobile-variant-badge-bottom');
    }

    if (this.sentinel instanceof HTMLElement) {
      const sentinelTop = this.sentinel.getBoundingClientRect().top + window.scrollY;
      this.dockStart = Math.max(0, Math.round(sentinelTop - (window.innerHeight - trayHeight)));
    } else {
      this.dockStart = 0;
    }

    this.updateDockState();
  }

  handleScroll() {
    if (this.rafId) return;

    this.rafId = window.requestAnimationFrame(() => {
      this.rafId = 0;
      this.updateDockState();
    });
  }

  updateDockState() {
    if (!mobileTrayQuery.matches || !(this.tray instanceof HTMLElement)) {
      this.host.classList.remove('is-docked');
      return;
    }

    this.host.classList.toggle('is-docked', window.scrollY >= this.dockStart);
  }

  syncBadgePlacement() {
    if (!(this.badge instanceof HTMLElement) || !(this.mount instanceof HTMLElement)) return;

    // Keep the actual variant fieldset inside the variant picker. Moving it out
    // can leave a stale copy behind when Shopify morphs the picker after a
    // variant/product change, which causes duplicate material selectors.
    this.restoreBadge();
  }

  restoreBadge() {
    if (!(this.badge instanceof HTMLElement) || !(this.badgeOriginParent instanceof HTMLElement)) return;
    if (this.badge.parentElement === this.badgeOriginParent) return;

    if (
      this.badgeOriginNextSibling instanceof HTMLElement
      && this.badgeOriginNextSibling.parentElement === this.badgeOriginParent
    ) {
      this.badgeOriginParent.insertBefore(this.badge, this.badgeOriginNextSibling);
      return;
    }

    this.badgeOriginParent.append(this.badge);
  }
}

const trayInstances = new WeakMap();

/**
 * @param {ParentNode} root
 */
function initPdpMobileTrays(root = document) {
  const hosts = root.querySelectorAll('[data-pdp-mobile-tray-host]');

  hosts.forEach((host) => {
    if (!(host instanceof HTMLElement) || trayInstances.has(host)) return;
    trayInstances.set(host, new PdpMobileTray(host));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initPdpMobileTrays(), { once: true });
} else {
  initPdpMobileTrays();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  initPdpMobileTrays(event.target);
});
